import * as History from 'history';
import { CurrencyContext, consumer, core } from '@tock/coretypes';
import { FlowRouteComponentProps } from 'components/render-prop-components/FlowRoute';
import { Head } from 'head/Head';
import { Link, Typography } from 'omakase';
import { LocalDateTime } from 'js-joda';
import { LockOptions } from 'types';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { find } from 'es-toolkit/compat';
import { toDateTimeString } from 'utils/timeUtils';
import { useEffect, useState } from 'react';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import RescheduleExperienceDetails from 'components/account/reschedule/RescheduleExperienceDetails';
import RescheduleHeader from 'components/account/reschedule/RescheduleHeader';
import ReservationPreCheckoutDetails from 'components/business/search/ReservationPreCheckoutDetails';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface OwnProps extends RouteComponentProps {
  app: StoreType['app'];
  business: StoreType['business'];
  offerings?: consumer.ConsumerOfferings;
  location: History.Location;
  lock: StoreType['lock'];
  onLockExpiration(): void;
  onSelectTime(
    size: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions
  ): Promise<void>;
  purchase: core.PastPurchase;
  reschedule: StoreType['reschedule'];
  selection: StoreType['selection'];
  showOptions: boolean;
}

type RescheduleStartRouteProps = OwnProps & RouteComponentProps & FlowRouteComponentProps;

const title = 'Modify your reservation';

const subtitle = (businessName: string, purchaseId: number, reservationCanBeCanceled: boolean) => {
  return (
    <>
      <Typography variant="body02" mb={2}>
        You may modify your reservation at {businessName} below. If you continue, the original
        reservation will be canceled and you will not be charged any fees.
      </Typography>
      <Typography variant="body02">
        If you want to modify your experience to a different experience at {businessName}, please{' '}
        {reservationCanBeCanceled ? (
          <Link href={`/profile/reservations/${purchaseId}/cancel`}>cancel this reservation</Link>
        ) : (
          <span>cancel this reservation</span>
        )}{' '}
        and book a new one.
      </Typography>
    </>
  );
};

const RescheduleStartRoute: React.FC<RescheduleStartRouteProps & DefaultConnectProps> = ({
  actions,
  app,
  business,
  location,
  lock,
  offerings,
  onLockExpiration,
  onSelectTime,
  purchase,
  selection,
  showOptions,
}) => {
  const history = useHistory();

  const parts = purchase.ticketDateTime.split('T');

  const [size, setSize] = useState(purchase.ticketCount ?? selection.size);
  const [date, setDate] = useState(selection.date ?? parts[0]);
  const [seatingAreaId, setSeatingAreaId] = useState<number | undefined>(
    purchase.seatingAreaId ?? undefined
  );

  const handleClickBack = () => {
    history.goBack();
  };

  const handleLockExpiration = () => {
    onLockExpiration();
  };

  const handleSearch = (size: number, date: string, time: string, seatingAreaId?: number) => {
    setSize(size);
    setDate(date);
    setSeatingAreaId(seatingAreaId);
  };

  const handleSelectTime = (
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions
  ): Promise<void> => {
    return onSelectTime(size, dateTime, priceCents, options);
  };

  useEffect(() => actions.navigation.incrementalUpdate({ title }), [actions.navigation]);

  const offering = offerings
    ? (find(offerings.experience, {
        id: purchase.ticketTypeId,
      }) as consumer.Offering)
    : undefined;

  const isLoading = !purchase || !offering || !business.business;

  const rescheduleOffering: consumer.Offering | undefined = offering
    ? {
        ...offering,
        price: {
          ...offering.price,
          pricePerPerson: {
            minCents: purchase.ticketTypePrice,
            maxCents: purchase.ticketTypePrice,
          },
        },
      }
    : undefined;
  let lockedDateTime;
  if (selection.date && selection.time) {
    lockedDateTime = toDateTimeString(selection.date, selection.time);
  } else if (size === purchase.ticketCount) {
    lockedDateTime = purchase.ticketDateTime;
  }

  const canChangePartySize = purchase.canEdit;

  return (
    <LoadingComponent isLoading={isLoading} isBallPulse>
      {!isLoading && (
        <div className="Reschedule-content PageContent-inner-bottom-only">
          <CurrencyContext.Provider value={business.business?.currencyCode ?? 'en-US'}>
            <Head title={`${business?.business?.name} - Reschedule`} />
            <RescheduleHeader
              location={location}
              lockedUntilMs={lock.lockedUntilMs ?? undefined}
              onClickBack={handleClickBack}
              onLockExpiration={handleLockExpiration}
              showOptions={showOptions}
              subtitle={subtitle(business.business?.name ?? '', purchase.id, purchase.canCancel)}
              title={title}
            />
            <div className="Reschedule-body--calendar" data-testid="reschedule-calendar-modal">
              <div className="Reschedule-body Reschedule-body--border">
                {rescheduleOffering && business.business && (
                  <RescheduleExperienceDetails
                    offering={rescheduleOffering}
                    business={business.business}
                  />
                )}
                {offerings && business.business ? (
                  <div className="Reschedule-body--borderBottom">
                    <ReservationPreCheckoutDetails
                      business={business.business}
                      canChangePartySize={canChangePartySize}
                      isSearching={false}
                      lockedDateTime={lockedDateTime}
                      offerings={offerings}
                      onSearch={handleSearch}
                      onSelectTime={handleSelectTime}
                      selectedDate={date}
                      selectedPurchaseSize={size}
                      selectedSeatingArea={seatingAreaId}
                      rescheduleOffering={rescheduleOffering}
                      purchase={purchase}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </CurrencyContext.Provider>
        </div>
      )}
    </LoadingComponent>
  );
};

export default connect<RescheduleStartRouteProps, DefaultConnectProps>(RescheduleStartRoute);
